<template>
  <section v-if="loading || hasData">
    <page-sub-header :title="sectionTitle" :subtitle="isDataReady ? timeSeriesSubtitle : undefined" class="mt-4">
      <template #header>
        <div class="d-flex flex-row align-items-center justify-content-end">
          <date-range-picker
            v-if="portalUpdatesJan2026Enabled"
            :from="dateFrom"
            :to="dateTo"
            :earliest="earliestAvailable"
            class="mr-3"
            @update:from="dateFrom = $event"
            @update:to="dateTo = $event"
          />
          <app-input
            v-model="timeSeries"
            :options="TIME_SERIES_VALUES"
            type="radio"
            buttons
            button-variant="outline-primary"
            class="text-uppercase mb-0"
            size="sm"
          />
          <export-button
            v-if="isDataReady && !portalUpdatesJan2026Enabled"
            :file-name="`coach-metrics-${timeSeries}`"
            :export-data="exportData"
            :export-file-rows="exportFileRows"
            :export-headers="exportHeaders"
            class="btn mt-0"
          />
        </div>
      </template>
    </page-sub-header>
    <sme-card v-if="loading">
      <app-loading />
    </sme-card>
    <template v-else-if="hasData">
      <!-- Row 1: Coach Users -->
      <div data-pdf-section="coach">
        <chart-with-companion-kpis
          v-if="timeGroupedCoachUsersDataset.values.length"
          card-class="mb-4"
          chart-title="Coach viewers over time"
          chart-subtitle="Number of employees"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedCoachUsersDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCoachUsersDataset.name } } },
              ...createCountWithPercentageTooltip(timeGroupedCoachUsersPercentageDataset),
            }"
            fill-gradient
          />
          <template #companion>
            <div class="stats-combined mb-3">
              <app-stat
                :stat="lastCoachUsers"
                :previous-stat="prevCoachUsers"
                :formatter="toInteger"
                :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Viewers`"
                background-color="core-1-lighten-90"
                color="default"
                pdf-kpi
              />
              <app-stat
                :stat="lastCoachUsersPercentage"
                :previous-stat="prevCoachUsersPercentage"
                :formatter="toNumber"
                suffix="%"
                :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Viewer Percentage`"
                background-color="core-1-lighten-90"
                color="default"
                pdf-kpi
              />
            </div>
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ toInteger(lastCoachUsers) }} employees ({{ lastCoachUsersPercentage }}%) viewed
              the coach.
              {{ coachUsersPrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>

        <!-- Coach Chat Participants (message senders) -->
        <chart-with-companion-kpis
          v-if="timeGroupedCoachChatUsersDataset.values.length"
          card-class="mb-4"
          chart-title="Coach chat participants over time"
          chart-subtitle="Number of employees"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedCoachChatUsersDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCoachChatUsersDataset.name } } },
              ...createCountWithPercentageTooltip(timeGroupedCoachChatUsersPercentageDataset),
            }"
            fill-gradient
          />
          <template #companion>
            <div class="stats-combined mb-3">
              <app-stat
                :stat="lastCoachChatUsers"
                :previous-stat="prevCoachChatUsers"
                :formatter="toInteger"
                :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Coach Chat Participants`"
                background-color="core-2-lighten-90"
                color="default"
                pdf-kpi
              />
              <app-stat
                :stat="lastCoachChatUsersPercentage"
                :previous-stat="prevCoachChatUsersPercentage"
                :formatter="toNumber"
                suffix="%"
                :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Chat Participant Percentage`"
                background-color="core-2-lighten-90"
                color="default"
                pdf-kpi
              />
            </div>
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ toInteger(lastCoachChatUsers) }} employees ({{ lastCoachChatUsersPercentage }}%)
              sent messages in the coach chat.
              {{ coachChatUsersPrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>
      </div>

      <!-- Education tiles: Courses Started, Top Courses, FHS Distribution -->
      <div v-if="hasEducationData" data-pdf-section="education">
        <page-sub-header v-if="hasCoachData" title="Education" title-tag="h2" class="mt-4" />
        <chart-with-companion-kpis
          v-if="timeGroupedCoursesStartedDataset.values.length"
          card-class="mb-4"
          chart-title="Courses started over time"
          chart-subtitle="Distinct (employee, course) pairs"
        >
          <line-chart
            :labels="coursesTimeGroupDataset"
            :datasets="timeGroupedCoursesStartedDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedCoursesStartedDataset.name } } },
            }"
            fill-gradient
          />
          <template #companion>
            <app-stat
              :stat="lastCoursesStarted"
              :previous-stat="prevCoursesStarted"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Courses Started`"
              class="mb-3"
              background-color="core-1-lighten-90"
              color="default"
              pdf-kpi
            />
            <p class="mb-0">
              {{ coursesTimeGroupTitle }}, employees started {{ toInteger(lastCoursesStarted) }} courses.
              {{ coursesStartedPrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>

        <b-row class="mb-4">
          <b-col cols="12" md="6" class="mb-4 mb-md-0">
            <div class="education-card-stack">
              <app-stat
                :stat="totalCoursesStarted"
                :formatter="toInteger"
                description="Courses Started · All time"
                background-color="core-2-lighten-20"
                pdf-kpi
              />
              <sme-card>
                <h6 class="mb-3 font-weight-bold">Top 5 Courses</h6>
                <ol v-if="topCourses.length" class="top-courses mb-0">
                  <li v-for="course in topCourses" :key="course.course_id">
                    <span class="top-courses__title">{{ course.title }}</span>
                    <span class="top-courses__count">{{ toInteger(course.learners) }} learners</span>
                  </li>
                </ol>
                <p v-else class="text-muted mb-0">Not enough data yet.</p>
              </sme-card>
            </div>
          </b-col>

          <b-col cols="12" md="6">
            <div class="education-card-stack">
              <app-stat
                v-if="fhsTotal > 0"
                :stat="fhsAverage"
                :formatter="toInteger"
                suffix=" / 40"
                :description="`Avg Financial Health Score · ${toInteger(fhsTotal)} employees`"
                background-color="core-3"
                pdf-kpi
              />
              <donut-distribution-card
                v-if="fhsTotal > 0"
                title="Financial Health Score"
                :labels="fhsBands.map(b => b.label)"
                :values="fhsBands.map(b => b.count)"
                :colors="fhsBandColors"
                compact
              />
              <sme-card v-else>
                <h6 class="mb-3 font-weight-bold">Financial Health Score</h6>
                <p class="text-muted mb-0">No FHS results yet.</p>
              </sme-card>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Row 2: Benefits Checker -->
      <div v-if="hasBenefitsData" data-pdf-section="benefits-checker">
        <page-sub-header
          v-if="portalUpdatesJan2026Enabled"
          title="Benefits Checker (InBest)"
          title-tag="h2"
          class="mt-4"
        />
        <chart-with-companion-kpis
          v-if="timeGroupedBenefitsCompletionsDataset.values.length"
          card-class="mb-4"
          chart-title="Benefits checker completions over time"
          chart-subtitle="Number of completions"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedBenefitsCompletionsDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: {
                y: { ticks: { precision: 0 }, title: { text: timeGroupedBenefitsCompletionsDataset.name } },
              },
            }"
            fill-gradient
          />
          <template #companion>
            <app-stat
              :stat="lastBenefitsCompletions"
              :previous-stat="prevBenefitsCompletions"
              :formatter="toInteger"
              :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Benefits Checker Completions`"
              class="mb-3"
              background-color="core-3-lighten-80"
              color="default"
              pdf-kpi
            />
            <app-stat
              :stat="totalEstimatedBenefits"
              :formatter="value => toInteger(Math.round(value))"
              :prefix="currencySymbol"
              description="Total estimated benefits uncovered"
              background-color="core-3-lighten-90"
              color="default"
              pdf-kpi
            />
            <p v-if="benefitsExplanation" class="mt-2 mb-0 text-muted small">
              {{ benefitsExplanation }}
            </p>
          </template>
        </chart-with-companion-kpis>
      </div>

      <!-- Row 3: Debt Advice Clicks -->
      <div v-if="hasDebtData" data-pdf-section="debt-advice">
        <page-sub-header
          v-if="portalUpdatesJan2026Enabled"
          title="Debt Advice (StepChange)"
          title-tag="h2"
          class="mt-4"
        />
        <chart-with-companion-kpis
          v-if="timeGroupedDebtCharityDataset.values.length"
          card-class="mb-4"
          chart-title="Debt advice link clicks over time"
          chart-subtitle="Number of clicks"
        >
          <line-chart
            :labels="timeGroupDataset"
            :datasets="timeGroupedDebtCharityDataset"
            :chart-options="{
              aspectRatio: getInitialAspectRatio(3, 2),
              scales: { y: { ticks: { precision: 0 }, title: { text: timeGroupedDebtCharityDataset.name } } },
              ...createCountWithPercentageTooltip(timeGroupedDebtCharityPercentageDataset),
            }"
            fill-gradient
          />
          <template #companion>
            <div class="stats-combined mb-3">
              <app-stat
                :stat="lastDebtCharityClicks"
                :previous-stat="prevDebtCharityClicks"
                :formatter="toInteger"
                :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Debt Support Referrals`"
                background-color="core-4-lighten-90"
                color="default"
                pdf-kpi
              />
              <app-stat
                :stat="lastDebtCharityPercentage"
                :previous-stat="prevDebtCharityPercentage"
                :formatter="toNumber"
                suffix="%"
                :description="`${timeSeriesUnit === 'week' ? 'Weekly' : 'Monthly'} Debt Support Referral Rate`"
                background-color="core-4-lighten-90"
                color="default"
                pdf-kpi
              />
            </div>
            <p class="mb-0">
              {{ timeGroupTitle }}, {{ toInteger(lastDebtCharityClicks) }} employees ({{ lastDebtCharityPercentage }}%)
              were referred to debt support.
              {{ debtCharityPrevDiffText }}
            </p>
          </template>
        </chart-with-companion-kpis>
      </div>
    </template>
  </section>
</template>

<script>
import { markerSection } from '@/utils/analyticsSections';

// Single source of truth for CoachMetrics' subsections, imported by both the
// Education tab's `sectionSpecs` and the homepage "Download all" capture host.
export function coachMetricsSections(coachRef) {
  return [
    markerSection(coachRef, {
      key: 'coach',
      label: 'Coach',
      hasDataKey: 'hasCoachData',
      exportTableKey: 'getCoachExportTable',
    }),
    markerSection(coachRef, {
      key: 'education',
      label: 'Education',
      hasDataKey: 'hasEducationData',
      exportTableKey: 'getEducationExportTable',
    }),
    markerSection(coachRef, {
      key: 'benefits-checker',
      label: 'Benefits Checker',
      hasDataKey: 'hasBenefitsData',
      exportTableKey: 'getBenefitsExportTable',
    }),
    markerSection(coachRef, {
      key: 'debt-advice',
      label: 'Debt Advice',
      hasDataKey: 'hasDebtData',
      exportTableKey: 'getDebtExportTable',
    }),
  ];
}
</script>

<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import ApiClient from '@/ApiClient';
import { PaletteColors } from '@/Theme';
import AppInput from '@/components/AppInput.vue';
import DateRangePicker from '@/components/DateRangePicker.vue';
import AppLoading from '@/components/AppLoading.vue';
import AppStat from '@/components/AppStat.vue';
import ChartWithCompanionKpis from '@/components/ChartWithCompanionKpis.vue';
import DonutDistributionCard from '@/components/DonutDistributionCard.vue';
import PageSubHeader from '@/components/PageSubHeader.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';
import LineChart from '@/components/charts/LineChart.vue';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useTimeGroupDataset from '@/composables/useTimeGroupDataset';
import useDateRangeSelection from '@/state/composables/useDateRangeSelection';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import {
  createCountWithPercentageTooltip,
  getDatasetColor,
  getInitialAspectRatio,
  TIME_SERIES_VALUES,
} from '@/utils/chart';
import { currencySymbolFromCode, toInteger, toNumber } from '@/utils/common';
import State from '@/state/State';
import ExportButton from '@/components/ExportButton.vue';

const { portalUpdatesJan2026Enabled } = useFeatureFlags();
const { divisionId } = useDivisionSelection();
const { from: dateFrom, to: dateTo, apiFrom, apiTo } = useDateRangeSelection();

const loading = ref(true);
const timeDataset = ref({ values: [] });
const coachUsersDataset = ref({ values: [], name: 'Coach viewers' });
const coachUsersPercentageDataset = ref({ values: [], name: 'Percentage (%)' });
const benefitsCompletionsDataset = ref({ values: [], name: 'Benefits checker completions' });
const entitledCountDataset = ref({ values: [], name: 'Entitled count' });
const estimatedBenefitsDataset = ref({ values: [], name: 'Estimated benefits (£)' });
const debtCharityDataset = ref({ values: [], name: 'Debt charity clicks' });
const debtCharityPercentageDataset = ref({ values: [], name: 'Percentage (%)' });
const coachChatUsersDataset = ref({ values: [], name: 'Coach chat participants' });
const coachChatUsersPercentageDataset = ref({ values: [], name: 'Percentage (%)' });
const coursesStartedDataset = ref({ values: [], name: 'Courses started' });
// Courses Started tracks its own time grid: course-takers can arrive via email
// links long before (or after) the Coach app has any activity, so its buckets
// often span a different range than the shared coach-users axis. Plotting it on
// the shared axis lines values up against the wrong months.
const coursesTimeDataset = ref({ values: [] });
const topCourses = ref([]);
const totalCoursesStarted = ref(0);
const fhsBands = ref([]);
const fhsTotal = ref(0);
const fhsAverage = ref(0);
const earliestAvailable = ref(null);

// Worst-to-best ramp matching the five hardcoded bands.
// Same colour ordering as the Loans page's loan-purpose pie chart so the
// two donut cards feel visually consistent (`getDatasetColor` is what
// SdlMetrics uses to colour each pie slice).
const fhsBandColors = Array.from({ length: 5 }, (_, index) => PaletteColors[getDatasetColor(index)]);

const {
  timeGroupDataset,
  timeGroupTitle,
  timeSeries,
  timeSeriesUnit,
  timeSeriesSubtitle,
  datasetToTimeGroupedDataset,
  getPrevDiffText,
} = useTimeGroupDataset(timeDataset);

// Dedicated time group for Courses Started, driven by that endpoint's own time
// grid. Shares the w2w/m2m `timeSeries` ref so it reacts to the same toggle.
const {
  timeGroupDataset: coursesTimeGroupDataset,
  timeGroupTitle: coursesTimeGroupTitle,
  datasetToTimeGroupedDataset: coursesDatasetToTimeGroupedDataset,
  getPrevDiffText: coursesGetPrevDiffText,
} = useTimeGroupDataset(coursesTimeDataset, timeSeries);

const currencySymbol = computed(() => currencySymbolFromCode(State.state.company.default_currency));

// Coach Users
const timeGroupedCoachUsersDataset = computed(() => datasetToTimeGroupedDataset(coachUsersDataset));
const timeGroupedCoachUsersPercentageDataset = computed(() => datasetToTimeGroupedDataset(coachUsersPercentageDataset));
const lastCoachUsers = computed(() => timeGroupedCoachUsersDataset.value?.calculated.last || 0);
const prevCoachUsers = computed(() => timeGroupedCoachUsersDataset.value?.calculated.prev);
const lastCoachUsersPercentage = computed(() => timeGroupedCoachUsersPercentageDataset.value?.calculated.last || 0);
const prevCoachUsersPercentage = computed(() => timeGroupedCoachUsersPercentageDataset.value?.calculated.prev);
const coachUsersPrevDiffText = computed(() => getPrevDiffText(timeGroupedCoachUsersDataset));

// Coach Chat Participants (actual message senders)
const timeGroupedCoachChatUsersDataset = computed(() => datasetToTimeGroupedDataset(coachChatUsersDataset));
const timeGroupedCoachChatUsersPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(coachChatUsersPercentageDataset),
);
const lastCoachChatUsers = computed(() => timeGroupedCoachChatUsersDataset.value?.calculated.last || 0);
const prevCoachChatUsers = computed(() => timeGroupedCoachChatUsersDataset.value?.calculated.prev);
const lastCoachChatUsersPercentage = computed(
  () => timeGroupedCoachChatUsersPercentageDataset.value?.calculated.last || 0,
);
const prevCoachChatUsersPercentage = computed(() => timeGroupedCoachChatUsersPercentageDataset.value?.calculated.prev);
const coachChatUsersPrevDiffText = computed(() => getPrevDiffText(timeGroupedCoachChatUsersDataset));

// Benefits Checker
const timeGroupedBenefitsCompletionsDataset = computed(() => datasetToTimeGroupedDataset(benefitsCompletionsDataset));
const timeGroupedEstimatedBenefitsDataset = computed(() => datasetToTimeGroupedDataset(estimatedBenefitsDataset));
const timeGroupedEntitledCountDataset = computed(() => datasetToTimeGroupedDataset(entitledCountDataset));
const lastBenefitsCompletions = computed(() => timeGroupedBenefitsCompletionsDataset.value?.calculated.last || 0);
const prevBenefitsCompletions = computed(() => timeGroupedBenefitsCompletionsDataset.value?.calculated.prev);
const totalEstimatedBenefits = computed(() => {
  const values = timeGroupedEstimatedBenefitsDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});
const totalCompletions = computed(() => {
  const values = timeGroupedBenefitsCompletionsDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});
const totalEntitledCount = computed(() => {
  const values = timeGroupedEntitledCountDataset.value?.values || [];
  return values.reduce((sum, val) => sum + (val || 0), 0);
});
const benefitsExplanation = computed(() => {
  if (totalCompletions.value <= 0 || totalEntitledCount.value <= 0) {
    return null;
  }
  const entitledPercentage = Math.round((totalEntitledCount.value / totalCompletions.value) * 100);
  const avgBenefitPerPerson = Math.round(totalEstimatedBenefits.value / totalEntitledCount.value);
  return `${entitledPercentage}% at ${currencySymbol.value}${avgBenefitPerPerson} per month`;
});

// Courses Started over time
const timeGroupedCoursesStartedDataset = computed(() => coursesDatasetToTimeGroupedDataset(coursesStartedDataset));
const lastCoursesStarted = computed(() => timeGroupedCoursesStartedDataset.value?.calculated.last || 0);
const prevCoursesStarted = computed(() => timeGroupedCoursesStartedDataset.value?.calculated.prev);
const coursesStartedPrevDiffText = computed(() => coursesGetPrevDiffText(timeGroupedCoursesStartedDataset));

// Debt Advice
const timeGroupedDebtCharityDataset = computed(() => datasetToTimeGroupedDataset(debtCharityDataset));
const timeGroupedDebtCharityPercentageDataset = computed(() =>
  datasetToTimeGroupedDataset(debtCharityPercentageDataset),
);
const lastDebtCharityClicks = computed(() => timeGroupedDebtCharityDataset.value?.calculated.last || 0);
const prevDebtCharityClicks = computed(() => timeGroupedDebtCharityDataset.value?.calculated.prev);
const lastDebtCharityPercentage = computed(() => timeGroupedDebtCharityPercentageDataset.value?.calculated.last || 0);
const prevDebtCharityPercentage = computed(() => timeGroupedDebtCharityPercentageDataset.value?.calculated.prev);
const debtCharityPrevDiffText = computed(() => getPrevDiffText(timeGroupedDebtCharityDataset));

// Read "has any rows" off raw dataset refs (which are initialised with
// empty arrays) rather than the timeGrouped* computeds — those depend on
// timeGroupDataset, which only populates once getCoachMetrics resolves.
const hasCoachData = computed(
  () => coachUsersDataset.value.values.length > 0 || coachChatUsersDataset.value.values.length > 0,
);

const hasEducationData = computed(
  () => coursesStartedDataset.value.values.length > 0 || topCourses.value.length > 0 || fhsTotal.value > 0,
);

const hasBenefitsData = computed(() => benefitsCompletionsDataset.value.values.length > 0);
const hasDebtData = computed(() => debtCharityDataset.value.values.length > 0);

const hasData = computed(
  () => hasCoachData.value || hasEducationData.value || hasBenefitsData.value || hasDebtData.value,
);

const sectionTitle = computed(() => 'Coach');

watch([divisionId, timeSeries, dateFrom, dateTo], () => getCoachMetrics());

const getCoachMetrics = async () => {
  loading.value = true;

  // allSettled so one failed endpoint doesn't blank the whole dashboard —
  // the Education routes are new and we don't want a 500 there taking down
  // the stable Coach Users / Benefits / Debt tiles too.
  const settled = await Promise.allSettled([
    ApiClient.getPortalCoachUsers(timeSeries.value, divisionId.value, apiFrom.value, apiTo.value),
    ApiClient.getPortalCoachChatUsers(timeSeries.value, divisionId.value, apiFrom.value, apiTo.value),
    ApiClient.getPortalCoachBenefits(timeSeries.value, divisionId.value, apiFrom.value, apiTo.value),
    ApiClient.getPortalDebtCharity(timeSeries.value, divisionId.value, apiFrom.value, apiTo.value),
    ApiClient.getPortalCoachCoursesStarted(timeSeries.value, divisionId.value, apiFrom.value, apiTo.value),
    ApiClient.getPortalCoachTopCourses(divisionId.value, 5),
    ApiClient.getPortalCoachFhsDistribution(divisionId.value),
  ]);

  settled.forEach(result => {
    if (result.status === 'rejected') {
      console.error('Coach metrics endpoint failed:', result.reason);
    }
  });

  const [
    coachUsersResponse,
    coachChatUsersResponse,
    benefitsResponse,
    debtCharityResponse,
    coursesStartedResponse,
    topCoursesResponse,
    fhsResponse,
  ] = settled.map(result => (result.status === 'fulfilled' ? result.value : null));

  try {
    earliestAvailable.value = coachUsersResponse?.earliest_available ?? null;

    // Coach Users - API returns coach_users and enrolled
    let enrolledData = null;
    if (coachUsersResponse?.results?.length) {
      const time = coachUsersResponse.results.find(result => result.id === 'time');
      const coachUsers = coachUsersResponse.results.find(result => result.id === 'coach_users');
      const enrolled = coachUsersResponse.results.find(result => result.id === 'enrolled');

      if (time) {
        timeDataset.value = time;
      }

      if (enrolled) {
        enrolledData = enrolled;
      }

      if (coachUsers) {
        coachUsersDataset.value = {
          ...coachUsers,
          name: 'Coach viewers',
          lineColor: PaletteColors['core-1'],
        };

        // Calculate percentage
        if (enrolled && enrolled.values.length) {
          const percentages = coachUsers.values.map((val, i) => {
            const enrolledCount = enrolled.values[i] || 0;
            return enrolledCount > 0 ? Math.round((val / enrolledCount) * 1000) / 10 : 0;
          });
          coachUsersPercentageDataset.value = {
            values: percentages,
            name: 'Percentage (%)',
            lineColor: PaletteColors['core-1'],
          };
        }
      }
    }

    // Coach Chat Users (actual message senders) - from portal-coach-chat-users endpoint
    if (coachChatUsersResponse?.results?.length) {
      const chatUsers = coachChatUsersResponse.results.find(result => result.id === 'chat_users');

      if (chatUsers) {
        coachChatUsersDataset.value = {
          ...chatUsers,
          name: 'Coach chat participants',
          lineColor: PaletteColors['core-2'],
        };

        // Calculate percentage using enrolled from coach users response
        if (enrolledData && enrolledData.values.length) {
          const percentages = chatUsers.values.map((val, i) => {
            const enrolledCount = enrolledData.values[i] || 0;
            return enrolledCount > 0 ? Math.round((val / enrolledCount) * 1000) / 10 : 0;
          });
          coachChatUsersPercentageDataset.value = {
            values: percentages,
            name: 'Percentage (%)',
            lineColor: PaletteColors['core-2'],
          };
        }
      }
    }

    // Benefits Checker - API returns completions, entitled_count, and estimated_benefits
    if (benefitsResponse?.results?.length) {
      const completions = benefitsResponse.results.find(result => result.id === 'completions');
      const entitledCount = benefitsResponse.results.find(result => result.id === 'entitled_count');
      const estimatedBenefits = benefitsResponse.results.find(result => result.id === 'estimated_benefits');

      if (completions) {
        benefitsCompletionsDataset.value = {
          ...completions,
          name: 'Benefits checker completions',
          lineColor: PaletteColors['core-3'],
        };
      }

      if (entitledCount) {
        entitledCountDataset.value = {
          ...entitledCount,
          name: 'Entitled count',
        };
      }

      if (estimatedBenefits) {
        estimatedBenefitsDataset.value = {
          ...estimatedBenefits,
          name: `Estimated benefits (${currencySymbol.value})`,
          lineColor: PaletteColors['core-3'],
        };
      }
    }

    // Debt Advice - API returns clicks only (use enrolled from coach users)
    if (debtCharityResponse?.results?.length) {
      const clicks = debtCharityResponse.results.find(result => result.id === 'clicks');

      if (clicks) {
        debtCharityDataset.value = {
          ...clicks,
          name: 'Debt advice link clicks',
          lineColor: PaletteColors['core-4'],
        };

        // Calculate percentage using enrolled from coach users response
        if (enrolledData && enrolledData.values.length) {
          const percentages = clicks.values.map((val, i) => {
            const enrolledCount = enrolledData.values[i] || 0;
            return enrolledCount > 0 ? Math.round((val / enrolledCount) * 1000) / 10 : 0;
          });
          debtCharityPercentageDataset.value = {
            values: percentages,
            name: 'Percentage (%)',
            lineColor: PaletteColors['core-4'],
          };
        }
      }
    }

    // Courses Started over time - API returns time and courses_started
    if (coursesStartedResponse?.results?.length) {
      const time = coursesStartedResponse.results.find(result => result.id === 'time');
      const courses = coursesStartedResponse.results.find(result => result.id === 'courses_started');

      if (time) {
        // Courses Started always plots against its own time grid (see
        // coursesTimeDataset) rather than the shared coach-users axis.
        coursesTimeDataset.value = time;

        // Also seed the shared axis when Coach Users returned nothing, so the
        // remaining coach tiles still have a grid — course-takers can arrive
        // via email links without ever opening the Coach app.
        if (timeDataset.value.values.length === 0) {
          timeDataset.value = time;
        }
      }

      if (courses) {
        coursesStartedDataset.value = {
          ...courses,
          name: 'Courses started',
          lineColor: PaletteColors['core-5'] || PaletteColors['core-3'],
        };
      }
    }

    // Top Courses - API returns {results: [...], total_started}
    if (topCoursesResponse) {
      topCourses.value = topCoursesResponse.results || [];
      totalCoursesStarted.value = topCoursesResponse.total_started || 0;
    }

    // FHS Distribution - API returns {bands: [...], total, average}
    if (fhsResponse) {
      fhsBands.value = (fhsResponse.bands || []).map(b => ({ label: b.label, count: b.count }));
      fhsTotal.value = fhsResponse.total || 0;
      fhsAverage.value = fhsResponse.average || 0;
    }
  } catch (error) {
    console.error('Failed to parse coach metrics:', error);
  }

  loading.value = false;
};

onBeforeMount(() => {
  getCoachMetrics();
});

const isDataReady = computed(() => {
  return (
    timeGroupDataset.value?.values &&
    coachUsersDataset.value?.values &&
    benefitsCompletionsDataset.value?.values &&
    debtCharityDataset.value?.values &&
    !loading.value
  );
});

const exportData = computed(() => {
  if (!isDataReady.value) {
    return [];
  }

  return timeGroupDataset.value.values.map((time, index) => ({
    time,
    coachViewers: coachUsersDataset.value.values[index] || 0,
    coachViewersPercentage: coachUsersPercentageDataset.value.values[index] || 0,
    coachChatParticipants: coachChatUsersDataset.value.values[index] || 0,
    coachChatParticipantsPercentage: coachChatUsersPercentageDataset.value.values[index] || 0,
    benefitsCompletions: benefitsCompletionsDataset.value.values[index] || 0,
    estimatedBenefits: estimatedBenefitsDataset.value.values[index] || 0,
    debtCharityClicks: debtCharityDataset.value.values[index] || 0,
    debtCharityPercentage: debtCharityPercentageDataset.value.values[index] || 0,
  }));
});

const exportHeaders = computed(() => {
  if (!isDataReady.value) {
    return [];
  }
  return [
    'Date',
    'Coach Viewers',
    'Coach Viewers (%)',
    'Coach Chat Participants',
    'Coach Chat Participants (%)',
    'Benefits Checker Completions',
    `Estimated Benefits (${currencySymbol.value})`,
    'Debt Support Referrals',
    'Debt Support Referral Rate (%)',
  ];
});

const exportFileRows = item => [
  item.time,
  item.coachViewers,
  item.coachViewersPercentage,
  item.coachChatParticipants,
  item.coachChatParticipantsPercentage,
  item.benefitsCompletions,
  Math.round(item.estimatedBenefits || 0),
  item.debtCharityClicks,
  item.debtCharityPercentage,
];

function getExportTable() {
  if (!isDataReady.value) {
    return null;
  }
  return {
    headers: exportHeaders.value,
    rows: exportData.value.map(exportFileRows),
  };
}

function getCoachExportTable() {
  if (!isDataReady.value || !hasCoachData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Coach Viewers', 'Coach Viewers (%)', 'Coach Chat Participants', 'Coach Chat Participants (%)'],
    rows: exportData.value.map(item => [
      item.time,
      item.coachViewers,
      item.coachViewersPercentage,
      item.coachChatParticipants,
      item.coachChatParticipantsPercentage,
    ]),
  };
}

function getEducationExportTable() {
  if (!isDataReady.value || !hasEducationData.value) {
    return null;
  }

  // hasEducationData is true when any of courses-started, top-courses, or FHS
  // has rows, so include each block that's present rather than emitting an
  // all-zero courses-started table for employers whose only education signal
  // is FHS or top courses.
  let headers = null;
  const rows = [];

  const pushSection = (sectionHeaders, sectionRows) => {
    if (!headers) {
      headers = sectionHeaders;
    } else {
      rows.push([]);
      rows.push(sectionHeaders);
    }
    rows.push(...sectionRows);
  };

  if (coursesStartedDataset.value.values.length > 0) {
    pushSection(
      ['Date', 'Courses Started'],
      coursesTimeGroupDataset.value.values.map((time, index) => [
        time,
        timeGroupedCoursesStartedDataset.value?.values?.[index] || 0,
      ]),
    );
  }

  if (topCourses.value.length > 0) {
    pushSection(
      ['Top Courses', 'Learners'],
      topCourses.value.map(course => [course.title, course.learners]),
    );
  }

  if (fhsTotal.value > 0) {
    pushSection(
      ['Financial Health Score', 'Value'],
      [
        ['Employees', fhsTotal.value],
        ['Average score (out of 40)', fhsAverage.value],
        ...fhsBands.value.map(b => [`Band: ${b.label}`, b.count]),
      ],
    );
  }

  if (!headers) {
    return null;
  }

  return { headers, rows };
}

function getBenefitsExportTable() {
  if (!isDataReady.value || !hasBenefitsData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Benefits Checker Completions', `Estimated Benefits (${currencySymbol.value})`],
    rows: exportData.value.map(item => [item.time, item.benefitsCompletions, Math.round(item.estimatedBenefits || 0)]),
  };
}

function getDebtExportTable() {
  if (!isDataReady.value || !hasDebtData.value) {
    return null;
  }
  return {
    headers: ['Date', 'Debt Support Referrals', 'Debt Support Referral Rate (%)'],
    rows: exportData.value.map(item => [item.time, item.debtCharityClicks, item.debtCharityPercentage]),
  };
}

defineExpose({
  hasData,
  getExportTable,
  hasCoachData,
  hasEducationData,
  hasBenefitsData,
  hasDebtData,
  getCoachExportTable,
  getEducationExportTable,
  getBenefitsExportTable,
  getDebtExportTable,
});
</script>

<style lang="scss" scoped>
.stats-combined {
  display: flex;

  :deep(.app-stat) {
    flex: 1;
    height: auto; // Override height: 100% from AppStat to allow flexbox stretch
    border-radius: 0;
    box-shadow: none;

    &:first-child {
      border-radius: 4px 0 0 4px;
      border-right: 1px solid rgba(0, 0, 0, 0.1);
    }

    &:last-child {
      border-radius: 0 4px 4px 0;
    }

    .app-stat__value-container .app-stat__value {
      font-size: 28px;

      @media (min-width: 992px) {
        font-size: 32px;
      }
    }

    .app-stat__value-container {
      height: auto;
      min-height: 2rem;
    }
  }
}

.education-card-stack {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  // app-stat carries height: 100%, which would otherwise stretch each
  // tile to its b-col height (set by the taller column) and shove the
  // sme-card below it off-screen. Compound :deep selector wins on
  // specificity — same pattern .stats-combined uses above.
  :deep(.app-stat) {
    height: auto;
    min-height: 9rem;
  }
}

.top-courses {
  padding-left: 1.25rem;
  margin: 0;

  li {
    margin-bottom: 0.5rem;
    line-height: 1.3;
  }

  &__title {
    display: block;
    font-weight: 500;
  }

  &__count {
    display: block;
    font-size: 0.85rem;
    color: var(--palette-color-default-lighten-30);
  }
}
</style>
